import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authClient, useAuth } from "@/context/AuthContext";

/**
 * WishlistContext — single source of truth for the shopper's saved
 * products. Fetches `/me/wishlist` once when the user logs in, then
 * exposes:
 *   - `items`: full list of saved products (drives the /wishlist page)
 *   - `count`: number of saved items (drives the header badge)
 *   - `has(product)`: has this product/slug been saved?
 *   - `toggle(product)`: optimistic add/remove; sends POST/DELETE
 *   - `remove(productId)`: explicit remove (used from /wishlist page)
 *   - `clear()`: wipe every saved item (used after "Move all to bag")
 *   - `refresh()`: manual refetch (used after login)
 *
 * Every heart button in the app reads from here so PLP hearts stay
 * filled without a per-card API roundtrip, and the /wishlist page
 * consumes the same array so it never drifts from the header badge.
 */
const WishlistContext = createContext(null);

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const r = await authClient.get("/me/wishlist");
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (_e) {
      // Silent — logged-out probes 401 which is expected.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Only re-fetch when the shopper's identity actually changes.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Derived id/slug sets — memoised so `has()` stays O(1).
  // Ids are always stored as strings — see WishlistContext docstring and
  // routes/wishlist.py; JS Number can't round-trip 64-bit product ids.
  const { ids, slugs } = useMemo(() => {
    const _ids = new Set();
    const _slugs = new Set();
    for (const it of items) {
      if (it?.product_id != null) _ids.add(String(it.product_id));
      if (it?.slug) _slugs.add(it.slug);
    }
    return { ids: _ids, slugs: _slugs };
  }, [items]);

  const has = useCallback(
    (p) => {
      if (!p) return false;
      if (p.id != null && ids.has(String(p.id))) return true;
      if (p.slug && slugs.has(p.slug)) return true;
      return false;
    },
    [ids, slugs],
  );

  const toggle = useCallback(
    async (product) => {
      if (!user) {
        window.location.href = "/login?next=" + encodeURIComponent(window.location.pathname);
        return false;
      }
      if (!product?.id) return false;
      const already = has(product);
      const snapshot = items;

      const pid = String(product.id);

      // Optimistic update — compare ids as strings (see note above).
      if (already) {
        setItems((prev) => prev.filter((it) => String(it.product_id) !== pid));
      } else {
        setItems((prev) => [
          {
            product_id: pid,
            slug: product.slug,
            name: product.name,
            image: product.image || product.images?.[0] || null,
            price: product.price,
          },
          ...prev.filter((it) => String(it.product_id) !== pid),
        ]);
      }

      try {
        if (already) {
          await authClient.delete(`/me/wishlist/${pid}`);
        } else {
          await authClient.post("/me/wishlist", {
            product_id: pid,
            slug: product.slug,
            name: product.name,
            image: product.image || product.images?.[0] || null,
            price: product.price,
          });
        }
        return !already;
      } catch (_e) {
        // Revert on failure
        setItems(snapshot);
        return already;
      }
    },
    [user, has, items],
  );

  const remove = useCallback(
    async (productId) => {
      if (!user || productId == null) return false;
      const pid = String(productId);
      const snapshot = items;
      setItems((prev) => prev.filter((it) => String(it.product_id) !== pid));
      try {
        await authClient.delete(`/me/wishlist/${encodeURIComponent(pid)}`);
        return true;
      } catch (_e) {
        setItems(snapshot);
        return false;
      }
    },
    [user, items],
  );

  const clear = useCallback(async () => {
    if (!user) return false;
    const snapshot = items;
    setItems([]);
    try {
      await Promise.all(
        snapshot
          .map((it) => (it?.product_id != null ? String(it.product_id) : null))
          .filter(Boolean)
          .map((pid) =>
            authClient.delete(`/me/wishlist/${encodeURIComponent(pid)}`).catch(() => null),
          ),
      );
      return true;
    } catch (_e) {
      setItems(snapshot);
      return false;
    }
  }, [user, items]);

  const value = useMemo(
    () => ({
      items,
      count: items.length,
      loading,
      has,
      toggle,
      remove,
      clear,
      refresh,
    }),
    [items, loading, has, toggle, remove, clear, refresh],
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    // Safe defaults so a hook call outside the provider never crashes
    return {
      items: [],
      count: 0,
      loading: false,
      has: () => false,
      toggle: async () => false,
      remove: async () => false,
      clear: async () => false,
      refresh: () => {},
    };
  }
  return ctx;
};

export default WishlistContext;
